import { useEffect, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2 } from "lucide-react";
import { AnimatedDialog } from "./AnimatedDialog";
import { Button } from "./Button";
import { cn } from "@/lib/utils";

export type ConfirmDialogTone = "info" | "warning" | "danger";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Caller is responsible for closing on confirm; useful for async actions. */
  onConfirm: () => void;
  tone?: ConfirmDialogTone;
  confirmDisabled?: boolean;
  /** Show a spinner inside the confirm button and disable interaction while true. */
  confirmLoading?: boolean;
  /** Label shown on the confirm button while loading. Defaults to confirmLabel + "ing…". */
  confirmLoadingLabel?: string;
  /** Hide the cancel button for notification-style dialogs with a single action. */
  showCancel?: boolean;
  /**
   * When set, the user must type this exact phrase before Confirm is enabled.
   * Used for irreversible actions (e.g. hard-delete provider).
   */
  confirmPhrase?: string;
  /** Label above the confirm-phrase input. */
  confirmPhraseLabel?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  tone = "info",
  confirmDisabled = false,
  confirmLoading = false,
  confirmLoadingLabel,
  showCancel = true,
  confirmPhrase,
  confirmPhraseLabel,
}: ConfirmDialogProps) {
  const loadingLabel = confirmLoadingLabel ?? `${confirmLabel}ing\u2026`;
  const [phraseInput, setPhraseInput] = useState("");

  // Reset typed phrase whenever the dialog closes or the expected phrase changes.
  useEffect(() => {
    if (!open) setPhraseInput("");
  }, [open, confirmPhrase]);

  const phraseOk = !confirmPhrase || phraseInput === confirmPhrase;
  const confirmBlocked = confirmDisabled || confirmLoading || !phraseOk;

  return (
    <AnimatedDialog open={open} onOpenChange={onOpenChange} maxWidth="max-w-md">
      <div className="p-6">
        <Dialog.Title
          className={cn(
            "text-base font-semibold",
            tone === "danger"
              ? "text-red-700"
              : tone === "warning"
                ? "text-amber-700"
                : "text-[var(--color-text-primary)]"
          )}
        >
          {title}
        </Dialog.Title>
        <Dialog.Description asChild>
          <div className="mt-2 text-sm text-[var(--color-text-secondary)] leading-relaxed">
            {description}
          </div>
        </Dialog.Description>

        {confirmPhrase ? (
          <div className="mt-4">
            <label className="mb-1.5 block text-xs font-medium text-[var(--color-text-secondary)]">
              {confirmPhraseLabel ?? (
                <>
                  Type <span className="font-mono font-semibold">{confirmPhrase}</span> to confirm
                </>
              )}
            </label>
            <input
              type="text"
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
              disabled={confirmLoading}
              autoComplete="off"
              className="h-9 w-full rounded-md border border-[var(--color-border)] bg-white px-3 text-sm outline-none transition focus:border-[var(--color-action-primary)] disabled:opacity-50"
            />
          </div>
        ) : null}

        <div className="mt-6 flex items-center justify-end gap-2">
          {showCancel ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={confirmLoading}
            >
              {cancelLabel}
            </Button>
          ) : null}
          <Button variant="primary" size="sm" onClick={onConfirm} disabled={confirmBlocked}>
            {confirmLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirmLoading ? loadingLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </AnimatedDialog>
  );
}
